import { useState, useEffect } from 'react';
import { Col, Form, Row, Select, Button, Modal, Input, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';

const { Option } = Select;

const CreatableSelect = ({
  optionsList = [],
  label,
  formName,
  createFn,
  placeholder,
  modalTitle,
  modalInputLabel,
  modalInputPlaceholder,
  successMessage,
  errorMessage,
  form,
  required = true,
  requiredMessage,
  handleChange,
  onItemCreated,
}) => {
  const [localOptions, setLocalOptions] = useState([]);
  const [createdOptions, setCreatedOptions] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [newItemName, setNewItemName] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    const existingIds = new Set((optionsList || []).map(item => item.id));
    const newOptions = createdOptions.filter(item => !existingIds.has(item.id));
    setLocalOptions([...(optionsList || []), ...newOptions]);
  }, [optionsList, createdOptions]);

  const handleCreate = async () => {
    if (!newItemName.trim()) return;
    setCreating(true);
    try {
      const response = await createFn({ name: newItemName });
      
      if (!response || !response.id) {
        throw new Error('Respuesta del servidor inválida o incompleta');
      }

      setCreatedOptions(prev => [...prev, response]);
      
      if (form) {
        form.setFieldsValue({
          [formName]: response.id,
          [`_${formName}`]: response.id
        });
      }
      
      handleChange?.(response.id);
      
      onItemCreated?.(response.id, response);
      
      setModalVisible(false);
      setNewItemName('');
      message.success(successMessage || 'Creado exitosamente');
    } catch (error) {
      console.error(error);
      message.error(errorMessage || 'Error al crear el elemento');
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <Row>
        <Col lg={22} xs={22}>
          <Form.Item
            name={formName}
            label={label}
            className="mb-0"
            rules={[{ required, message: requiredMessage || `Campo ${label} es requerido` }]}
          >
            <Select
              className="cmt-select"
              placeholder={placeholder || `Seleccione ${label.toLowerCase()}`}
              showSearch
              allowClear
              onChange={handleChange}
              filterOption={(input, option) => option.children?.toLowerCase().includes(input.toLowerCase())}
            >
              {localOptions?.map(item => (
                <Option key={item.id} value={item.id}>{item.name}</Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col lg={2} xs={2} className="plus-button-container">
          <Form.Item label="&nbsp;" className="mb-0">
            <Button block icon={<PlusOutlined />} onClick={() => setModalVisible(true)} />
          </Form.Item>
        </Col>
      </Row>

      <Modal
        className="creation-modal"
        title={null}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
      >
        <h1 className="mb-20 mt-20 text-center">{modalTitle || `Crear ${label}`}</h1>
        <Form layout="vertical">
          <Form.Item label={modalInputLabel || `Nombre de ${label.toLowerCase()}`} required>
            <Input 
              placeholder={modalInputPlaceholder || `Ingrese nombre de ${label.toLowerCase()}`} 
              value={newItemName} 
              onChange={e => setNewItemName(e.target.value)} 
              className="cmt-input"
            />
          </Form.Item>
          <Row gutter={16} justify="center" className="mt-20">
            <Col span={10}>
              <Button block className="cmt-button primary outline" type="primary" onClick={() => setModalVisible(false)}>
                Cancelar
              </Button>
            </Col>
            <Col span={10}>
              <Button block type="primary" className="cmt-button primary" onClick={handleCreate} loading={creating}>
                Crear
              </Button>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

export default CreatableSelect;
